import { useScrollReveal } from "@/hooks/useScrollReveal";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Textarea } from "@/components/ui/textarea";
import {
  Select,
  SelectContent,
  SelectItem,
  SelectTrigger,
  SelectValue,
} from "@/components/ui/select";
import { ArrowRight, MapPin, Calendar } from "lucide-react";
import { useState } from "react";
import { EnfinityPrimaryBtn } from "@/components/design-system/enfinity/EnfinityButton";

const AZURE_CDN = "https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity";

export default function GetInTouch() {
  const { ref, isVisible } = useScrollReveal();
  const [formData, setFormData] = useState({
    firstName: "",
    lastName: "",
    email: "",
    subject: "",
    message: "",
  });

  const handleInputChange = (e) => {
    setFormData({ ...formData, [e.target.name]: e.target.value });
  };

  const handleSelectChange = (value) => {
    setFormData({ ...formData, subject: value });
  };

  const sendMail = () => {
    const subject = encodeURIComponent(
      formData.subject || "Enfinity Website: Inquiries"
    );
    const body = encodeURIComponent(
      `Name: ${formData.firstName} ${formData.lastName}
Email: ${formData.email}
Subject: ${formData.subject}
Message:
${formData.message}`
    );

    const gmailURL = `https://mail.google.com/mail/?view=cm&fs=1&to=requests@enfinity.com&su=${subject}&body=${body}`;
    window.open(gmailURL, "_blank");

    setFormData({
      firstName: "",
      lastName: "",
      email: "",
      subject: "",
      message: "",
    });
  };

  return (
    <div
      ref={ref}
      className={`${
        isVisible ? "" : ""
      } bg-white py-20 lg:py-32 px-4 xxs:px-8 xs:px-10 sm:px-10 md:px-10 lg:px-12 xl:px-12 1xl:px-12 2xl:px-16 4xl:px-16`}
    >
      <div className="mx-auto max-w-7xl">
        <div className="flex flex-col lg:flex-row lg:gap-16 items-start">
          {/* Left Column - Get in touch */} 
          <h2 className="text-5xl xxs:text-[55px] lg:text-[64px] font-normal text-black mb-6 enfinity-header-font block lg:hidden">
              Get in touch
            </h2>
            <p className="text-lg xxs:text-[1rem] lg:text-xl text-black mb-8 leading-relaxed max-w-[599px] enfinity-subtext-font block lg:hidden">
              Our team is ready to answer your questions and help you get started
              with Enfinity's creator platform.
            </p>
          <div className="flex-1 w-full lg:w-auto order-2 lg:order-1">
            <h2 className="text-5xl md:text-6xl lg:text-[64px] 4xl:text-[90px] font-normal  text-black mb-6 enfinity-header-font hidden lg:block">
              Get in touch
            </h2>
            <p className="text-lg md:text-xl text-black mb-8 leading-relaxed max-w-[599px] enfinity-subtext-font hidden lg:block">
              Our team is ready to answer your questions and help you get started
              with Enfinity's creator platform.
            </p>

            {/* Contact Cards */}
            <div className="flex flex-col gap-4 my-8">
              {/* Email Card */}
              <div className="bg-white border border-[#eee] rounded-[20px] shadow-[0px_2px_6px_0px_rgba(144,146,148,0.05)] h-[110px] lg:h-[131px] 4xl:h-[120px] flex items-center gap-4 lg:gap-6 px-4 lg:px-6">
                <div
                  className="flex items-center justify-center w-[60px] h-[60px] lg:w-[75px] lg:h-[75px] rounded-[18px] p-1 shrink-0"
                  style={{
                    backgroundImage:
                      "linear-gradient(137.738deg, rgb(0, 166, 86) 2.173%, rgb(9, 145, 41) 52.175%, rgb(18, 126, 0) 97.827%)",
                  }}
                >
                  <img src="https://cdn-vvv-e9hucnd2c4dye2ek.z01.azurefd.net/assets/enfinity/mailbox.svg" alt="Email" className="w-8 h-8 lg:w-10 lg:h-10" />
                </div>
                <div className="flex flex-col gap-2 lg:gap-3">
                  <p className="font-semibold text-sm lg:text-base xxs:text-xs xs:text-base sm:text-base md:text-lg 4xl:text-[16px] text-black enfinity-header-font">Email us at</p>
                  <p className="text-sm lg:text-base xxs:text-[10px] xs:text-[10px] sm:text-[10px] md:text-lg 4xl:text-[14px] text-black leading-[20px] lg:leading-[32px] xxs:leading-[10px] xs:leading-[18px] sm:leading-[15px] md:leading-[30px] enfinity-subtext-font">
                  requests@enfinity.com
                  </p>
                </div>
              </div>

              {/* Office Location Card */}
              <div className="bg-white border border-[#eee] rounded-[20px] shadow-[0px_2px_6px_0px_rgba(144,146,148,0.05)] h-[110px] lg:h-[131px] 4xl:h-[120px] flex items-center gap-4 lg:gap-6 px-4 lg:px-6">
                <div
                  className="flex items-center justify-center w-[60px] h-[60px] lg:w-[75px] lg:h-[75px] rounded-[18px] p-1 shrink-0"
                  style={{
                    backgroundImage:
                      "linear-gradient(137.738deg, rgb(96, 165, 250) 2.173%, rgb(59, 130, 246) 97.827%)",
                  }}
                >
                  <MapPin className="w-8 h-8 lg:w-10 lg:h-10 text-white" strokeWidth={1} />
                </div>
                <div className="flex flex-col gap-2 lg:gap-3">
                  <p className="font-semibold text-sm lg:text-base xxs:text-[12px] xs:text-[15px] sm:text-base md:text-lg 4xl:text-[16px] xxs:leading-[10px] xs:leading-[20px] sm:leading-[28px] md:leading-[30px] lg:leading-[32px] text-black enfinity-header-font">
                    Office location
                  </p>
                  <p className="text-sm lg:text-base xxs:text-[9px] xs:text-[10px] sm:text-[10px] md:text-lg 4xl:text-[14px] text-black leading-[20px] lg:leading-normal xxs:leading-[12px] xs:leading-[15px] sm:leading-[15px] md:leading-[30px] enfinity-subtext-font">
                    18 Whitfield Road, Causeway Bay, Room 1607-8, 16th Floor,
                    Wan Guo Baotong Center, Causeway Bay, Hong Kong
                  </p>
                </div>
              </div>

              {/* Business Hours Card */}
              <div className="bg-white border border-[#eee] rounded-[20px] shadow-[0px_2px_6px_0px_rgba(144,146,148,0.05)] h-[110px] lg:h-[131px] 4xl:h-[120px] flex items-center gap-4 lg:gap-6 px-4 lg:px-6">
                <div
                  className="flex items-center justify-center w-[60px] h-[60px] lg:w-[75px] lg:h-[75px] rounded-[18px] p-1 shrink-0"
                  style={{
                    backgroundImage:
                      "linear-gradient(137.738deg, rgb(167, 139, 250) 2.173%, rgb(139, 92, 246) 97.827%)",
                  }}
                >
                  <Calendar className="w-8 h-8 lg:w-10 lg:h-10 text-white" strokeWidth={1} />
                </div>
                <div className="flex flex-col gap-2 lg:gap-3">
                  <p className="font-semibold text-sm lg:text-base xxs:text-[12px] xs:text-[15px] sm:text-base md:text-lg 4xl:text-[16px] xxs:leading-[10px] xs:leading-[24px] sm:leading-[28px] md:leading-[30px] lg:leading-[32px] text-black enfinity-header-font">
                    Business Hours
                  </p>
                  <div className="text-sm lg:text-base xxs:text-[10px] xs:text-[10px] sm:text-[10px] md:text-lg 4xl:text-[14px] xxs:leading-[15px] xs:leading-[15px] sm:leading-[15px] md:leading-[30px] lg:leading-[32px] text-black enfinity-subtext-font">
                    <p className="mb-1 leading-tight">Monday - Friday</p>
                    <p className="mt-0 mb-0 leading-tight">9:00 AM - 6:00 PHT</p>
                  </div>
                </div>
              </div>
            </div>

            {/* Social Media */}
            <div>
              <p className="text-lg font-medium text-black mb-4 enfinity-subtext-font">
                Follow our social media
              </p>
              <div className="flex flex-wrap gap-2 items-center">
                <a href="https://www.facebook.com/profile.php?id=61587027306800" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-12 h-12 rounded-lg transition-colors" aria-label="Facebook">
                  <img src={`${AZURE_CDN}/sc-facebook.svg`} alt="Facebook" className="w-12 h-12 object-contain" />
                </a>
                <a href="https://www.youtube.com/@enfinity" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-12 h-12 rounded-lg transition-colors" aria-label="YouTube">
                  <img src={`${AZURE_CDN}/sc-youtube.svg`} alt="YouTube" className="w-12 h-12 object-contain" />
                </a>
                <a href="https://www.tiktok.com/@enfinitylimited?is_from_webapp=1&sender_device=pc" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-12 h-12 rounded-lg transition-colors" aria-label="TikTok">
                  <img src={`${AZURE_CDN}/sc-tiktok.svg`} alt="TikTok" className="w-12 h-12 object-contain" />
                </a>
                <a href="https://www.instagram.com/enfinitylimited?utm_source=ig_web_button_share_sheet&igsh=ZDNlZDc0MzIxNw==" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-12 h-12 rounded-lg transition-colors" aria-label="Instagram">
                  <img src={`${AZURE_CDN}/sc-instagram.svg`} alt="Instagram" className="w-12 h-12 object-contain" />
                </a>
                <a href="https://mail.google.com/mail/u/0/?fs=1&to=requests@enfinity.com&tf=cm" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-12 h-12 rounded-lg transition-colors" aria-label="Email">
                  <img src={`${AZURE_CDN}/sc-telegram.svg`} alt="Email" className="w-10 h-10 object-contain" />
                </a>
                <a href="https://api.whatsapp.com/send/?phone=639291514853" target="_blank" rel="noopener noreferrer" className="flex items-center justify-center w-12 h-12 rounded-lg transition-colors" aria-label="WhatsApp">
                  <img src={`${AZURE_CDN}/sc-whatsapp.svg`} alt="WhatsApp" className="w-10 h-10 object-contain" />
                </a>
              </div>
            </div>
          </div>

          {/* Right Column - Send us a message */}
          <div className="flex-1 w-full lg:w-auto order-1 lg:order-2">
            <div className="bg-white border border-[#eee] rounded-[20px] shadow-[0px_2px_6px_0px_rgba(0,0,0,0.1)] p-10">
              <h2 className="text-3xl xxs:text-[18px] xs:text-2xl sm:text-2xl md:text-4xl lg:text-[32px] font-normal text-black mb-3 enfinity-header-font">
                Send us a message
              </h2>
              <p className="text-base xxs:text-[12px] xs:text-[15px] sm:text-[16px] md:text-base lg:text-base text-[rgba(0,0,0,0.6)] mb-8 enfinity-subtext-font">
                Fill out the form below and we'll respond within 24 hours.
              </p>

              <form
                onSubmit={(e) => {
                  e.preventDefault();
                  sendMail();
                }}
                className="flex flex-col gap-[25px]"
              >
                {/* First Name and Last Name */}
                <div className="flex gap-[27px]">
                  <div className="flex-1 flex flex-col gap-[10px]">
                    <Label className="text-lg font-medium text-black enfinity-header-font">
                      First Name
                    </Label>
                    <Input
                      type="text"
                      name="firstName"
                      placeholder="Ex. John"
                      value={formData.firstName}
                      onChange={handleInputChange}
                      className="rounded-[10px] bg-white border border-[#ddd] px-[29px] py-[23px] text-sm text-[rgba(0,0,0,0.5)] focus-visible:ring-0 focus-visible:border-[#00D115]"
                      required
                    />
                  </div>
                  <div className="flex-1 flex flex-col gap-[10px]">
                    <Label className="text-lg font-medium text-black enfinity-header-font">
                      Last Name
                    </Label>
                    <Input
                      type="text"
                      name="lastName"
                      placeholder="Ex. Roberts"
                      value={formData.lastName}
                      onChange={handleInputChange}
                      className="rounded-[10px] bg-white border border-[#ddd] px-[29px] py-[23px] text-sm text-[rgba(0,0,0,0.5)] focus-visible:ring-0 focus-visible:border-[#00D115]"
                      required
                    />
                  </div>
                </div>

                {/* Email Address */}
                <div className="flex flex-col gap-[10px]">
                  <Label className="text-lg font-medium text-black enfinity-header-font">
                    Email Address
                  </Label>
                  <Input
                    type="email"
                    name="email"
                    placeholder="Please enter a valid email address"
                    value={formData.email}
                    onChange={handleInputChange}
                    className="rounded-[10px] bg-white border border-[#ddd] px-[29px] py-[23px] text-sm text-[rgba(0,0,0,0.5)] focus-visible:ring-0 focus-visible:border-[#00D115]"
                    required
                  />
                </div>

                {/* Subject */}
                <div className="flex flex-col gap-[10px]">
                  <Label className="text-lg font-medium text-black enfinity-header-font">
                    Subject
                  </Label>
                  <Select
                    value={formData.subject}
                    onValueChange={handleSelectChange}
                    required
                  >
                    <SelectTrigger className={`w-full rounded-[10px] bg-white border border-[#ddd] px-[29px] py-[23px] text-sm focus-visible:ring-0 focus-visible:border-[#00D115] ${formData.subject ? 'text-black' : 'text-[rgba(0,0,0,0.5)]'}`}>
                      <SelectValue placeholder="Select a subject" />
                    </SelectTrigger>
                    <SelectContent className="bg-white border border-[#ddd] rounded-[10px] shadow-lg">
                      <SelectItem value="general" className="px-[29px] py-[12px] text-sm text-black cursor-pointer hover:bg-gray-50 focus:bg-gray-50">
                        General Inquiry
                      </SelectItem>
                      <SelectItem value="enfinipay" className="px-[29px] py-[12px] text-sm text-black cursor-pointer hover:bg-gray-50 focus:bg-gray-50">
                        Enfinipay - Payment Questions
                      </SelectItem>
                      <SelectItem value="enfiniboost" className="px-[29px] py-[12px] text-sm text-black cursor-pointer hover:bg-gray-50 focus:bg-gray-50">
                        EnfiniBoost - Distribution Questions
                      </SelectItem>
                      <SelectItem value="enfinishield" className="px-[29px] py-[12px] text-sm text-black cursor-pointer hover:bg-gray-50 focus:bg-gray-50">
                        EnfiniShield - Copyright Questions
                      </SelectItem>
                      <SelectItem value="technical-support" className="px-[29px] py-[12px] text-sm text-black cursor-pointer hover:bg-gray-50 focus:bg-gray-50">
                        Technical Support
                      </SelectItem>
                      <SelectItem value="partnership" className="px-[29px] py-[12px] text-sm text-black cursor-pointer hover:bg-gray-50 focus:bg-gray-50">
                        Partnership Inquiry
                      </SelectItem>
                    </SelectContent>
                  </Select>
                </div>

                {/* Message */}
                <div className="flex flex-col gap-[10px]">
                  <Label className="text-lg font-medium text-black enfinity-header-font">
                    Message
                  </Label>
                  <Textarea
                    name="message"
                    placeholder="Tell us how we can help..."
                    value={formData.message}
                    onChange={handleInputChange}
                    className="rounded-[10px] bg-white border border-[#ddd] px-[29px] py-[23px] text-sm text-[rgba(0,0,0,0.5)] focus-visible:ring-0 focus-visible:border-[#00D115]"
                    required
                  />
                </div>

                {/* Submit Button */}
                <EnfinityPrimaryBtn type="submit" className="w-full">
                  <span>Send us a message</span>
                  <ArrowRight className="w-6 h-6 enfinity-subtext-font" />
                </EnfinityPrimaryBtn>
              </form>
            </div>
          </div>
        </div>
      </div>
    </div>
  );
}
